/**
 * Shared language types and utilities for multilingual content.
 *
 * Used by KB, News, Events, POI, Surveys, and other content modules across
 * municipality and super-admin apps. All language codes follow ISO 639-1 format.
 */

const LANGUAGE_CODE_REGEX = /^[a-z]{2}$/;

/**
 * Interface for language option configuration.
 */
export interface LanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  direction?: 'ltr' | 'rtl';
}

/**
 * Supported languages. Add new languages here when extending the system.
 */
export const AVAILABLE_LANGUAGES: readonly LanguageOption[] = Object.freeze([
  Object.freeze({
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr',
  }),
  Object.freeze({
    code: 'el',
    name: 'Greek',
    nativeName: 'Ελληνικά',
    flag: '🇬🇷',
    direction: 'ltr',
  }),
]);

const LANGUAGE_CODES_SET = new Set(
  AVAILABLE_LANGUAGES.map((lang) => lang.code)
);

export const PRIMARY_LANGUAGE = 'en' as const;

export interface LanguageSettings {
  primaryLanguage: string;
  secondaryLanguages: string[];
  isMultilingual: boolean;
}

export const DEFAULT_LANGUAGE_SETTINGS: LanguageSettings = {
  primaryLanguage: PRIMARY_LANGUAGE,
  secondaryLanguages: [],
  isMultilingual: false,
};

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function isValidLanguageCode(code: string): boolean {
  return typeof code === 'string' && LANGUAGE_CODE_REGEX.test(code);
}

export function normalizeLanguageCode(code: string): string {
  if (typeof code !== 'string') {
    throw new Error(`Invalid language code type: expected string, got ${typeof code}`);
  }
  const normalized = code.toLowerCase().trim();
  if (!isValidLanguageCode(normalized)) {
    throw new Error(
      `Invalid language code format: "${code}". Expected lowercase ISO 639-1 format (e.g. 'en', 'el')`
    );
  }
  return normalized;
}

export function isLanguageSupported(code: string): boolean {
  try {
    const normalized = normalizeLanguageCode(code);
    return LANGUAGE_CODES_SET.has(normalized);
  } catch {
    return false;
  }
}

export function validateLanguageSettings(
  settings: Partial<LanguageSettings> | null | undefined
): ValidationResult {
  const errors: string[] = [];

  if (!settings || typeof settings !== 'object') {
    errors.push('Language settings must be an object');
    return { valid: false, errors };
  }

  if (!settings.primaryLanguage) {
    errors.push('primaryLanguage is required');
  } else if (typeof settings.primaryLanguage !== 'string') {
    errors.push('primaryLanguage must be a string');
  } else {
    if (!isValidLanguageCode(settings.primaryLanguage)) {
      errors.push(
        `Invalid primaryLanguage format: "${settings.primaryLanguage}". Expected lowercase ISO 639-1 format.`
      );
    } else if (!isLanguageSupported(settings.primaryLanguage)) {
      errors.push(
        `Unsupported primaryLanguage: "${settings.primaryLanguage}". Language is not available.`
      );
    }
  }

  if (settings.secondaryLanguages !== undefined) {
    if (!Array.isArray(settings.secondaryLanguages)) {
      errors.push('secondaryLanguages must be an array');
    } else {
      const seen = new Set<string>();
      settings.secondaryLanguages.forEach((lang, index) => {
        if (typeof lang !== 'string') {
          errors.push(`secondaryLanguages[${index}] must be a string`);
          return;
        }
        if (!isValidLanguageCode(lang)) {
          errors.push(
            `Invalid language code in secondaryLanguages[${index}]: "${lang}". Expected lowercase ISO 639-1 format.`
          );
          return;
        }
        if (!isLanguageSupported(lang)) {
          errors.push(
            `Unsupported language in secondaryLanguages[${index}]: "${lang}". Language is not available.`
          );
          return;
        }
        if (lang === settings.primaryLanguage) {
          errors.push(
            `secondaryLanguages[${index}] cannot be the same as primaryLanguage: "${lang}"`
          );
          return;
        }
        if (seen.has(lang)) {
          errors.push(`secondaryLanguages contains duplicate value: "${lang}"`);
        } else {
          seen.add(lang);
        }
      });
    }
  }

  if (
    settings.isMultilingual !== undefined &&
    typeof settings.isMultilingual !== 'boolean'
  ) {
    errors.push('isMultilingual must be a boolean');
  }

  if (settings.isMultilingual === true) {
    if (!settings.secondaryLanguages || settings.secondaryLanguages.length === 0) {
      errors.push(
        'isMultilingual is true but secondaryLanguages is empty. Add secondary languages or set isMultilingual to false.'
      );
    }
  }

  if (settings.secondaryLanguages && settings.secondaryLanguages.length > 0) {
    if (settings.isMultilingual === false) {
      errors.push(
        'secondaryLanguages are specified but isMultilingual is false. Remove secondary languages or set isMultilingual to true.'
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

export function sanitizeLanguageSettings(
  settings: Partial<LanguageSettings> | null | undefined
): LanguageSettings {
  if (!settings || typeof settings !== 'object') {
    return { ...DEFAULT_LANGUAGE_SETTINGS };
  }

  let primaryLanguage: string = PRIMARY_LANGUAGE;
  try {
    if (settings.primaryLanguage) {
      const normalized = normalizeLanguageCode(settings.primaryLanguage);
      if (isLanguageSupported(normalized)) {
        primaryLanguage = normalized;
      }
    }
  } catch {
    primaryLanguage = PRIMARY_LANGUAGE;
  }

  let secondaryLanguages: string[] = [];
  if (Array.isArray(settings.secondaryLanguages)) {
    secondaryLanguages = settings.secondaryLanguages
      .map((lang) => {
        try {
          return normalizeLanguageCode(lang);
        } catch {
          return null;
        }
      })
      .filter(
        (lang): lang is string =>
          lang !== null &&
          isLanguageSupported(lang) &&
          lang !== primaryLanguage
      )
      .filter((lang, index, arr) => arr.indexOf(lang) === index);
  }

  const isMultilingual =
    settings.isMultilingual === true && secondaryLanguages.length > 0;
  const finalSecondaryLanguages = isMultilingual ? secondaryLanguages : [];

  return {
    primaryLanguage,
    secondaryLanguages: finalSecondaryLanguages,
    isMultilingual,
  };
}
